import { schemaStepName } from '@/components/claims/newClaim/schema/schemaStepName';
import { getNewClaimMultiStepFormSchema } from '@/components/claims/newClaim/schema/steps';
import { getNewClaimStepMedicalExpensesFormSchema } from '@/components/claims/newClaim/schema/steps/stepMedicalExpenses';
import { getNewClaimStepOtherExpensesFormSchema } from '@/components/claims/newClaim/schema/steps/stepOtherExpenses';
import { getNewClaimStepPaymentContactFormSchema } from '@/components/claims/newClaim/schema/steps/stepPaymentContact';
import {
  additionalExpenseLabels,
  cancellationExpenseLabels,
  medicalExpenseLabels,
} from '@/themes/shared/data/constants/expenseTypeLabels';
import {
  type DocumentCategory,
  type DocumentInfo,
  type DraftClaimAdditionalExpense,
  type DraftClaimCancellationExpense,
  type DraftClaimMedicalExpense,
  type DraftClaimSummary,
  type FormStepSchema,
  type RequiredDocumentsByCategory,
  SchemaAdditionalExpenseType,
  SchemaCancellationExpenseType,
  SchemaProviderAccountType,
  type UserDraftClaim,
  type UserPolicyDetails,
  type WatchedFormValues,
} from '@/types';
import claimReviewStepData from '@data-shared/newClaim/claimReviewStep';
import DOMPurify from 'dompurify';
import { isBoolean } from './assertions';
import { formatAmountOfMoney, formatBoolean, formatCurrency } from './common';
import { formatDateInClaim } from './date';
import { filterUploadedDocuments, getRequiredDocuments } from './document';
import { formatFieldValue } from './form';
import { getClaimFormStepStart, getClaimPaymentStep, getClaimUploadsStep } from './newClaim';

const DOCUMENT_CATEGORIES: DocumentCategory[] = ['evidence', 'expense'];

interface DraftClaimSummaryQuestionWithoutIndex {
  question: string;
  answers: string[];
}

interface DraftClaimSummarySectionWithoutIndex {
  name: string;
  questions: DraftClaimSummaryQuestionWithoutIndex[];
}

interface GetDraftClaimSummaryParams {
  allFieldValues: WatchedFormValues;
  completedDocuments: DocumentInfo[];
  claimPolicy?: UserPolicyDetails;
  draftClaim?: UserDraftClaim;
}

function generatePolicyDetailsSummarySection(
  claimPolicy: UserPolicyDetails,
): DraftClaimSummarySectionWithoutIndex {
  const { title, labels } = claimReviewStepData.policyDetails;
  const {
    policyNumber,
    policyStart,
    policyEnd,
    type,
    // @TODO: Remove the default values once they are returned by BFF
    excess = 0,
    planName = '',
    destinations = [],
    maxTripDuration = '',
  } = claimPolicy;
  const isAnnualMultiTrip = type === 'Annual';

  return {
    name: title,
    questions: [
      { question: labels.policyNumber, answers: [policyNumber] },
      {
        question: labels.destinations,
        answers: [destinations.map(({ name }) => name).join(', ')],
      },
      { question: labels.planName, answers: [planName] },
      { question: labels.excess, answers: [formatCurrency(excess)] },
      ...(isAnnualMultiTrip
        ? [
            { question: labels.policyStartDate, answers: [formatDateInClaim(policyStart)] },
            { question: labels.maxTripDuration, answers: [`Up to ${maxTripDuration} days`] },
          ]
        : [
            {
              question: labels.travelDates,
              answers: [`${formatDateInClaim(policyStart)} - ${formatDateInClaim(policyEnd)}`],
            },
          ]),
    ],
  };
}

function generateMedicalExpenseSection(
  medicalExpense: DraftClaimMedicalExpense,
): DraftClaimSummarySectionWithoutIndex {
  const {
    expenseType,
    expenseDate,
    expensePaidInFull,
    expenseAmount,
    claimAmount,
    providerOwedAmount,
    providerDetails,
  } = medicalExpense;
  const { labels } = claimReviewStepData.medicalExpense;
  const name = getNewClaimStepMedicalExpensesFormSchema().title;
  const providerAccountLabel =
    providerDetails?.accountType === SchemaProviderAccountType.IBAN
      ? labels.ibanNumber
      : labels.swiftCode;

  try {
    return {
      name,
      questions: [
        {
          question: labels.expenseType,
          answers: expenseType ? [medicalExpenseLabels[expenseType]] : [],
        },
        { question: labels.expenseDate, answers: [formatDateInClaim(expenseDate)] },
        { question: labels.expenseAmount, answers: [formatAmountOfMoney(expenseAmount)] },
        {
          question: labels.expensePaidInFull,
          answers: isBoolean(expensePaidInFull) ? [formatBoolean(expensePaidInFull)] : [],
        },
        ...(expensePaidInFull
          ? []
          : [
              { question: labels.accountName, answers: [providerDetails?.accountName ?? ''] },
              { question: providerAccountLabel, answers: [providerDetails?.accountNumber ?? ''] },
              {
                question: labels.referenceNumber,
                answers: [providerDetails?.referenceNumber ?? ''],
              },
              {
                question: labels.providerOwedAmount,
                answers: [formatAmountOfMoney(providerOwedAmount)],
              },
            ]),
        { question: labels.claimAmount, answers: [formatAmountOfMoney(claimAmount)] },
      ],
    };
  } catch (error) {
    console.error(error);
    return { name, questions: [] };
  }
}

function generateCancellationExpenseSection(
  cancellationExpense: DraftClaimCancellationExpense,
): DraftClaimSummarySectionWithoutIndex {
  const {
    expenseType,
    expenseDate,
    expenseDescription,
    expenseAmount,
    claimAmount,
    creditNoteOrRefundReceived,
    creditNoteOrRefundAmount,
  } = cancellationExpense;
  const { labels } = claimReviewStepData.cancellationExpense;

  return {
    name: getNewClaimStepOtherExpensesFormSchema().title,
    questions: [
      {
        question: labels.expenseType,
        answers: [expenseType ? cancellationExpenseLabels[expenseType] : ''],
      },
      ...(expenseType === SchemaCancellationExpenseType.Other
        ? [{ question: labels.expenseDescription, answers: [expenseDescription ?? ''] }]
        : []),
      {
        question: labels.expenseDate,
        answers: [expenseDate ? formatDateInClaim(expenseDate) : ''],
      },
      { question: labels.expenseAmount, answers: [formatAmountOfMoney(expenseAmount)] },
      {
        question: labels.creditNoteOrRefundReceived,
        answers: [formatBoolean(!!creditNoteOrRefundReceived)],
      },
      ...(creditNoteOrRefundReceived
        ? [
            {
              question: labels.creditNoteOrRefundAmount,
              answers: [formatAmountOfMoney(creditNoteOrRefundAmount)],
            },
          ]
        : []),
      { question: labels.claimAmount, answers: [formatAmountOfMoney(claimAmount)] },
    ],
  };
}

function generateAdditionalExpenseSection(
  additionalExpense: DraftClaimAdditionalExpense,
): DraftClaimSummarySectionWithoutIndex {
  const {
    expenseType,
    expenseDate,
    expenseDescription,
    expenseAmount,
    claimAmount,
    creditNoteOrRefundReceived,
    creditNoteOrRefundAmount,
  } = additionalExpense;
  const { labels } = claimReviewStepData.additionalExpense;

  return {
    name: getNewClaimStepOtherExpensesFormSchema().title,
    questions: [
      {
        question: labels.expenseType,
        answers: [expenseType ? additionalExpenseLabels[expenseType] : ''],
      },
      ...(expenseType === SchemaAdditionalExpenseType.Other
        ? [{ question: labels.expenseDescription, answers: [expenseDescription ?? ''] }]
        : []),
      {
        question: labels.expenseDate,
        answers: [expenseDate ? formatDateInClaim(expenseDate) : ''],
      },
      { question: labels.expenseAmount, answers: [formatAmountOfMoney(expenseAmount)] },
      {
        question: labels.creditNoteOrRefundReceived,
        answers: [formatBoolean(!!creditNoteOrRefundReceived)],
      },
      ...(creditNoteOrRefundReceived
        ? [
            {
              question: labels.creditNoteOrRefundAmount,
              answers: [formatAmountOfMoney(creditNoteOrRefundAmount)],
            },
          ]
        : []),
      { question: labels.claimAmount, answers: [formatAmountOfMoney(claimAmount)] },
    ],
  };
}

function generateFormStepSummarySection(
  formStepSchema: FormStepSchema,
  allFieldValues: WatchedFormValues,
): DraftClaimSummarySectionWithoutIndex {
  const questions = formStepSchema.form.fields
    .filter(field => field.name in allFieldValues && !field.layoutOptions?.isVisuallyHidden)
    .map(field => ({
      question: field.label ?? '',
      answers: formatFieldValue({ field, value: allFieldValues[field.name] }),
    }));

  return { name: formStepSchema.title, questions };
}

function generateDocumentsSummarySection(
  requiredDocuments: RequiredDocumentsByCategory,
  completedDocuments: DocumentInfo[],
): DraftClaimSummarySectionWithoutIndex {
  const { title } = claimReviewStepData.docUploads;
  const questions = DOCUMENT_CATEGORIES.flatMap(category => {
    const requiredDocumentTypes = requiredDocuments[category] ?? [];

    return requiredDocumentTypes.map(({ documentType, expenseId, amount }) => ({
      question: amount ? `${documentType} (${formatAmountOfMoney(amount)})` : documentType,
      answers: filterUploadedDocuments({
        uploadedDocuments: completedDocuments,
        category,
        documentType,
        expenseId,
      }).map(document => DOMPurify.sanitize(document.fileName)),
    }));
  });

  return { name: title, questions };
}

function getDraftClaimSummary({
  claimPolicy,
  allFieldValues,
  completedDocuments,
  draftClaim,
}: GetDraftClaimSummaryParams): DraftClaimSummary {
  if (!claimPolicy) {
    console.error('Claim policy is not found');
    return [];
  }

  const multiStepFormSchema = getNewClaimMultiStepFormSchema();
  const paymentContactFormSchema = getNewClaimStepPaymentContactFormSchema();
  const requiredDocuments = getRequiredDocuments(allFieldValues, draftClaim);

  const sections = [
    generatePolicyDetailsSummarySection(claimPolicy),
    ...multiStepFormSchema
      // Expense sections are excluded on purpose - we display them using values derived
      // from draftClaim instead down below
      .filter(formStepSchema => {
        return (
          formStepSchema.name !== schemaStepName.stepMedicalExpenses &&
          formStepSchema.name !== schemaStepName.stepOtherExpenses
        );
      })
      .map(formStepSchema => generateFormStepSummarySection(formStepSchema, allFieldValues)),

    ...(draftClaim?.expenses?.medicalExpenses?.length
      ? draftClaim.expenses.medicalExpenses.map(generateMedicalExpenseSection)
      : []),
    ...(draftClaim?.expenses?.cancellationExpenses?.length
      ? draftClaim.expenses.cancellationExpenses.map(generateCancellationExpenseSection)
      : []),
    ...(draftClaim?.expenses?.additionalExpenses?.length
      ? draftClaim.expenses.additionalExpenses.map(generateAdditionalExpenseSection)
      : []),
    generateDocumentsSummarySection(requiredDocuments, completedDocuments),
    generateFormStepSummarySection(paymentContactFormSchema, allFieldValues),
  ];

  return sections.map((section, sectionIndex) => ({
    index: sectionIndex,
    name: section.name,
    questions: section.questions.map((question, questionIndex) => ({
      index: questionIndex,
      question: question.question,
      answers: question.answers.map((answer, answerIndex) => ({
        index: answerIndex,
        answer,
      })),
    })),
  }));
}

function getStepsBySectionName(): Record<string, number> {
  const multiStepFormSchema = getNewClaimMultiStepFormSchema();
  const paymentContactFormSchema = getNewClaimStepPaymentContactFormSchema();

  return {
    ...Object.fromEntries(
      multiStepFormSchema.map((formStepSchema, stepIndex) => [
        formStepSchema.title,
        getClaimFormStepStart() + stepIndex,
      ]),
    ),
    [claimReviewStepData.docUploads.title]: getClaimUploadsStep(),
    [paymentContactFormSchema.title]: getClaimPaymentStep(),
  };
}

export { getDraftClaimSummary, getStepsBySectionName };
export type { GetDraftClaimSummaryParams };
